import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecruiterAuth } from '../contexts/RecruiterAuthContext';
import { useTheme } from '../contexts/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} aria-label="Toggle theme"
      className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
      style={{ border: '1px solid var(--lx-border)' }}>
      {theme === 'dark' ? '☀' : '🌙'}
    </button>
  );
}

const NAV = [
  { to: '/recruiter/dashboard', key: 'dashboard', icon: '📊' },
  { to: '/recruiter/jobs',      key: 'jobs',      icon: '💼' },
];

function AccountMenu() {
  const { t } = useTranslation();
  const { recruiter, logout } = useRecruiterAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click + on route change.
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const initials = (recruiter?.name || recruiter?.email || '?')
    .split(' ').map((s) => s[0]).join('').toUpperCase().slice(0, 2);
  const avatar = recruiter?.avatar_url;

  const go = (to) => { setOpen(false); navigate(to); };
  const onLogout = () => { setOpen(false); logout(); navigate('/recruiter/login'); };

  const Avatar = ({ size }) => (avatar
    ? <img src={avatar} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />
    : <span className="rounded-full bg-indigo-600 text-white text-xs font-semibold flex items-center justify-center" style={{ width: size, height: size }}>{initials}</span>);

  const Item = ({ icon, label, onClick }) => (
    <button onClick={onClick}
      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/[0.04] transition-colors text-left">
      <span className="opacity-70 w-4 text-center">{icon}</span>{label}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 pl-2 pr-2.5 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors">
        <Avatar size={32} />
        <span className="hidden sm:block text-left leading-tight">
          <span className="block text-xs text-gray-200 font-medium max-w-[140px] truncate">{recruiter?.name}</span>
          <span className="block text-[11px] text-gray-500 max-w-[140px] truncate">{recruiter?.company || recruiter?.email}</span>
        </span>
        <span className={`text-gray-500 text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-2xl shadow-2xl shadow-black/40 z-50 animate-fade-in overflow-hidden"
          style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--lx-border)' }}>
            <p className="text-sm font-semibold text-white truncate">{recruiter?.name}</p>
            <p className="text-xs text-gray-500 truncate">{recruiter?.email}</p>
            {recruiter?.company && <p className="text-xs text-gray-500 truncate mt-0.5">{recruiter.company}</p>}
          </div>
          <div className="py-1.5">
            <Item icon="👤" label={t('recruiter.account.editProfile')}    onClick={() => go('/recruiter/profile')} />
            <Item icon="🔑" label={t('recruiter.account.changePassword')} onClick={() => go('/recruiter/password')} />
          </div>
          <div className="py-1.5 border-t" style={{ borderColor: 'var(--lx-border)' }}>
            <button onClick={onLogout}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/[0.08] transition-colors text-left">
              <span className="opacity-80 w-4 text-center">⏻</span>{t('recruiter.account.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RecruiterLayout({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-lx-bg text-gray-200">
      <header style={{ background: 'var(--lx-card)', borderBottom: '1px solid var(--lx-border)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to="/recruiter/dashboard" className="flex items-center gap-2.5">
              <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="LearnXops" className="w-8 h-8 object-contain" />
              <div className="leading-none">
                <span className="text-sm font-semibold text-white">LearnXops</span>
                <span className="block text-[11px] text-indigo-400 font-medium mt-0.5">{t('recruiter.hub')}</span>
              </div>
            </Link>
            <nav className="hidden sm:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? 'text-indigo-400 bg-indigo-500/10' : 'text-gray-400 hover:text-gray-200'
                    }`
                  }>
                  <span className="mr-1.5 opacity-70">{n.icon}</span>{t('recruiter.nav.' + n.key)}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <AccountMenu />
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="sm:hidden flex items-center gap-1 px-4 pb-2">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  isActive ? 'text-indigo-400 bg-indigo-500/10' : 'text-gray-400'
                }`
              }>
              {n.icon} {t('recruiter.nav.' + n.key)}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">{children}</main>
    </div>
  );
}
